export interface Conta {
  id: string;
  nome: string;
  tipo: 'corrente' | 'poupanca' | 'caixa' | 'investimento' | 'cartao';
  banco: string | null;
  agencia: string | null;
  numero: string | null;
  /** M12.4: empresa do grupo dona da conta; null = do grupo */
  empresa_id: string | null;
  empresa: string | null;
  moeda: string;
  saldo_inicial_centavos: string;
  data_saldo_inicial: string;
  saldo_centavos: string;
  pendentes_conciliacao: number;
}

export interface Categoria {
  id: string;
  nome: string;
  tipo: 'receita' | 'despesa';
  parent_id: string | null;
  uso: number;
}

export interface CentroCusto {
  id: string;
  codigo: string;
  nome: string;
  uso: number;
  custo_liquido_centavos: string;
}

export interface Lancamento {
  id: string;
  numero: string;
  tipo: 'entrada' | 'saida' | 'transferencia';
  sentido: 'credito' | 'debito';
  descricao: string;
  valor_centavos: string;
  moeda: string;
  data_caixa: string;
  data_competencia: string;
  conta_id: string;
  conta: string;
  categoria_id: string | null;
  categoria: string | null;
  centro_custo_id: string | null;
  centro_custo: string | null;
  parceiro_id: string | null;
  parceiro: string | null;
  conciliado: boolean;
  estornado: boolean;
  estorno_de_id: string | null;
  transferencia_par_id: string | null;
  origem: string;
  comprovantes: number;
}

export interface LinhaExtrato {
  id: string;
  extrato_id: string;
  conta_id: string;
  conta: string;
  data: string;
  valor_centavos: string;
  moeda: string;
  descricao: string;
  estado: 'pendente' | 'conciliado';
  lancamento_id: string | null;
  lancamento_numero: string | null;
  sugestao: { lancamento_id: string; numero: string; distancia_dias: number; descricao: string } | null;
}

/**
 * ROB-R25-01: a sugestão de par é calculada por página. Um lançamento sugerido a uma linha de cima não volta como
 * sugestão de outra linha numa página seguinte (vira "sem sugestão"; a escolha manual continua disponível).
 */
export function semSugestaoRepetida(linhas: LinhaExtrato[]): LinhaExtrato[] {
  const usados = new Set<string>();
  return linhas.map((l) => {
    if (!l.sugestao) return l;
    if (usados.has(l.sugestao.lancamento_id)) return { ...l, sugestao: null };
    usados.add(l.sugestao.lancamento_id);
    return l;
  });
}

/**
 * Centavos (texto, com sinal) para moeda local sem passar por float: o Intl recebe o decimal exato em texto.
 * Com `sinal`, positivo ganha '+'.
 */
export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR', sinal = false): string {
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = BigInt(centavos);
  const neg = c < 0n;
  const a = neg ? -c : c;
  const decimal = `${neg ? '-' : ''}${(a / 100n).toString()}.${(a % 100n).toString().padStart(2, '0')}`;
  const fmt = new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda, ...(sinal ? { signDisplay: 'exceptZero' as const } : {}) });
  return fmt.format(decimal as unknown as number);
}

/** Símbolo da moeda no idioma (BRL 'R$', USD 'US$' em pt e '$' em en). Rótulo de valor segue a moeda da conta (SPEC1-R8-05). */
export function simboloMoeda(moeda = 'BRL', idioma = 'pt-BR'): string {
  try {
    return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? moeda;
  } catch {
    return moeda;
  }
}

/** Texto em reais ('1.234,56') para centavos inteiros em texto, sem float. 'invalido' se não reconhece. */
export function reaisParaCentavos(texto: string, permitirNegativo = false): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(-?)(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m || (m[1] && !permitirNegativo)) return 'invalido';
  const v = BigInt(m[2]!.replace(/\./g, '')) * 100n + BigInt((m[3] ?? '').padEnd(2, '0') || '0');
  return (m[1] ? -v : v).toString();
}

/** Efeito com sinal no saldo: crédito positivo, débito negativo. */
export const efeito = (l: Pick<Lancamento, 'sentido' | 'valor_centavos'>) => (l.sentido === 'credito' ? l.valor_centavos : `-${l.valor_centavos}`);

export const formatarData = (iso: string | null, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)) : '');

export const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
